import { useCallback, useState, useContext, useEffect, useRef } from "react"

//SOAR components:
import EnterpriseLibrarySidebar from "../../../components/sidebars/EnterpriseLibrarySidebar"
import PageSidebarContent from "../../../components/sidebars/PageSidebarContent"
import { LoaderMedium } from "../../../components/Loaders/index"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//PrimeReact
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Tooltip } from "primereact/tooltip"

//Housing API and components
import { housingApiListHousesPost } from "../housingApi"
import HouseFilters from "./HouseFilters"
import myStyle from "../../../styles/housing/HousingManagement.module.css"
import HouseForm from "./HouseForm"
import { getHouseImageUrl } from "../shared"

//Helpers:
import { LogError } from "../../../helpers/logger"

//GraphQL
import { useLazyQuery } from "@apollo/client"
import { GET_ORGANIZATIONS } from "../queries"
import { classNames } from "primereact/utils"

export default function HouseManagement() {
  //Retrieve relevant context values
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editHouseId, setEditHouseId] = useState(undefined)
  const [houses, setHouses] = useState(undefined)
  const [organizations, setOrganizations] = useState([])
  const [pageSetup, setPageSetup] = useState({ rows: 20, first: 0, page: 0 })

  const topScroll = useRef()

  const filters = useRef({
    organizationId: undefined,
    search: "",
  })

  const [getOrganizations] = useLazyQuery(GET_ORGANIZATIONS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
    },
  })

  const getHouses = useCallback(async () => {
    try {
      setIsLoading(true)
      let houseList = await housingApiListHousesPost(
        userCtx.tokens.getHousingToken(),
        userCtx.session.user.enterprise_id,
        filters.current.organizationId || "all",
        pageSetup.rows,
        pageSetup.page + 1,
        filters.current.search || "",
      )

      if (houseList.houses.length > 0) {
        houseList.houses.forEach((house) => {
          house.house.shortAddress =
            `${house.house.address}${
              !!house.house.city ? `;${house.house.city}` : ""
            }${!!house.house.state ? `;${house.house.state}` : ""}` || ""

          house.house.relativeImage = getHouseImageUrl(house.house.image)
        })
      }

      setHouses(houseList)
      setIsLoading(false)
    } catch (error) {
      LogError("Unable to retrieve Housing listing", error)
      userCtx.setNotificationError("Unable to retrieve Housing listing")
      setHouses(undefined)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userCtx.session.user.enterprise_id,
    pageSetup.page,
    pageSetup.rows,
    userCtx.setNotificationError,
  ])

  useEffect(() => {
    getHouses()
  }, [getHouses, userCtx.setNotificationError])

  useEffect(() => {
    getOrganizations({
      variables: {
        staff_id: userCtx.session.user.staff_id,
      },
    })
      .then((result) => {
        if (!!result.error) {
        } else {
          setOrganizations(
            result.data.organizationsByUid.filter(
              (x) =>
                x.enterprise_id === userCtx.session.user.enterprise_id &&
                x.organization_id !== x.enterprise_id,
            ),
          )
        }
      })
      .catch((error) => {
        LogError("Unable to retrieve organizations", error)
        userCtx.setNotificationError("Unable to retrieve organizations")
        setOrganizations([])
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userCtx.session.user.staff_id,
    userCtx.session.user.enterprise_id,
    getOrganizations,
  ])

  useEffect(() => {
    if (topScroll.current) {
      topScroll.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [topScroll])

  const onPageChange = (setup) => {
    setPageSetup(setup)
  }

  const ActionBodyTemplate = (rowData) => {
    if (rowData.house.id) {
      return (
        <div style={{ width: "100%" }}>
          <button
            type="button"
            onClick={() => {
              setEditHouseId(rowData.house.id)
              setShowForm(true)
            }}
            className="btn"
            style={{ float: "right" }}
            disabled={isLoading}
          >
            Edit
          </button>
        </div>
      )
    } else {
      return null
    }
  }

  const HouseAttributes = (rowData) => {
    return (
      <div className={myStyle.houseListAttributes}>
        <Tooltip target=".houseAttribute_tt" />
        <div
          className={`houseAttribute_tt ${myStyle.houseListAttributeItem}`}
          data-pr-tooltip="Rooms available in this house"
        >
          <span className={`material-icons`}>{"house"}</span>
          {`: ${rowData.roomCount}`}
        </div>
        <div
          className={`houseAttribute_tt ${myStyle.houseListAttributeItem}`}
          data-pr-tooltip="Beds available in this house"
        >
          <span className={`material-icons`}>{"bed"}</span>
          {`: ${rowData.bedCount}`}
        </div>
        <div
          className={`houseAttribute_tt ${myStyle.houseListAttributeItem}`}
          data-pr-tooltip="Participants allocated to beds in this house"
        >
          <span className={`material-icons`}>{"person"}</span>
          <label>{`: ${rowData.occupantCount}`}</label>
        </div>
      </div>
    )
  }

  const ImageBodyTemplate = (rowData) => {
    let image = getHouseImageUrl(rowData.houseImageUrl)

    return (
      <img
        className={classNames(
          "ratio-16-9",
          "selectDisable",
          myStyle.houseImage,
        )}
        src={image}
        alt="HouseImage"
      />
    )
  }

  return (
    <div ref={topScroll}>
      <PageSidebarContent>
        <EnterpriseLibrarySidebar />
      </PageSidebarContent>
      <div className="main-content">
        <h1>Housing: House management</h1>
        <HouseForm
          showForm={showForm}
          onHideForm={() => {
            setShowForm(false)
            setEditHouseId(undefined)
          }}
          houseId={editHouseId}
          organizations={organizations}
          onHouseCreated={() => {
            setShowForm(false)
            setEditHouseId(undefined)
            getHouses()
          }}
          houses={houses?.houses}
        />
        <HouseFilters
          isLoading={isLoading}
          filters={filters}
          organizations={organizations}
          onChangeFilters={(newFilters) => {
            if (
              filters.current.organizationId !== newFilters.organizationId ||
              filters.current.search !== newFilters.search
            ) {
              filters.current = newFilters
              getHouses()
            }
          }}
          onNewClick={() => {
            setEditHouseId(undefined)
            setShowForm(true)
          }}
          onRefreshClick={() => {
            getHouses()
          }}
        />

        {isLoading ? (
          <LoaderMedium />
        ) : (
          <DataTable
            value={!!houses ? houses.houses : []}
            lazy
            paginator
            first={(houses?.pageNum - 1) * houses?.pageSize}
            rows={houses?.pageSize}
            totalRecords={houses?.numTotal}
            onPage={onPageChange}
          >
            <Column
              body={ImageBodyTemplate}
              header="Image"
              style={{ width: "100px" }}
            />
            <Column field="house.name" header="Name" />
            <Column field="house.shortAddress" header="Address" />
            <Column
              body={HouseAttributes}
              header="Attributes"
              style={{ width: "250px" }}
            />
            <Column
              header=""
              body={ActionBodyTemplate}
              alignFrozen="right"
              frozen
              style={{ width: "100px" }}
              resizeable={false}
            />
          </DataTable>
        )}
      </div>
    </div>
  )
}
